import type {
  CollectionReference,
  DocumentData,
  Firestore,
  FirestoreDataConverter,
  QueryDocumentSnapshot,
} from 'firebase/firestore'
import { collection, query } from 'firebase/firestore'
import { getDocsWithError } from '../../firestore-mobx/fetch'
import {
  convertDocumentSnapshotToModel,
  modelListStream,
} from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { LMSConflict } from '../../models/LMSConflict'
import type { FirestoreLMSConflict } from './schema'
import { schema, writeSchema } from './schema'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreLMSConflict> = {
  toFirestore: (data) => {
    writeSchema.partial().parse(data)
    return data
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

const getColRef = (
  firestore: Firestore,
  sectionId: string,
  assignmentId: string
): CollectionReference<FirestoreLMSConflict, DocumentData> => {
  return collection(
    firestore,
    'section',
    sectionId,
    'assignment',
    assignmentId,
    'lms_conflict'
  ).withConverter(converter)
}

export const empty: FirestoreLMSConflict = {
  roomId: '',
  userId: '',
  createdAt: undefined,
  localScore: undefined,
  lmsScore: undefined,
  newScore: undefined,
}

/**
 * Get all LMS conflicts for a specific assignment
 */
export const getLMSConflicts = (
  repository: FirebaseRepository,
  { sectionId, assignmentId }: { sectionId: string; assignmentId: string }
) => {
  const ref = getColRef(repository.firestore, sectionId, assignmentId)
  const q = query(ref)

  return modelListStream(repository, q, LMSConflict)
}

/**
 * Fetch LMS conflicts for a specific assignment (one-time fetch, not streaming)
 */
export const fetchLMSConflicts = async (
  repository: FirebaseRepository,
  { sectionId, assignmentId }: { sectionId: string; assignmentId: string }
) => {
  const ref = getColRef(repository.firestore, sectionId, assignmentId)
  const q = query(ref)

  const snapshot = await getDocsWithError(q, 'FetchLMSConflictsError')

  return snapshot.docs.map((doc) => {
    return convertDocumentSnapshotToModel(repository, doc, LMSConflict)
  })
}
